import { Link } from 'react-router-dom';
import { ShoppingCart, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { formatPrice } from '../utils/formatPrice';

// Category badges configuration
const CATEGORY_BADGES = {
  'pompes-circulation': { label: 'Circulation', color: 'bg-blue-100 text-blue-700' },
  'pompes-massage': { label: 'Massage', color: 'bg-purple-100 text-purple-700' },
  'rechauffeurs': { label: 'Réchauffeur', color: 'bg-orange-100 text-orange-700' },
  'blower': { label: 'Blower', color: 'bg-teal-100 text-teal-700' },
  'blowers': { label: 'Blower', color: 'bg-teal-100 text-teal-700' },
  'claviers-commandes': { label: 'Clavier', color: 'bg-indigo-100 text-indigo-700' },
  'claviers': { label: 'Clavier', color: 'bg-indigo-100 text-indigo-700' },
  'cartes-electroniques': { label: 'Électronique', color: 'bg-pink-100 text-pink-700' },
  'boitiers-commande': { label: 'Boîtier', color: 'bg-slate-100 text-slate-700' },
  'filtres': { label: 'Filtre', color: 'bg-green-100 text-green-700' },
  'accessoires': { label: 'Accessoire', color: 'bg-amber-100 text-amber-700' },
  'entretien': { label: 'Entretien', color: 'bg-cyan-100 text-cyan-700' },
};

// Helper to add to cart
const addToCart = (product, e) => {
  e.preventDefault();
  e.stopPropagation();
  
  const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
  const existingIndex = cart.findIndex(item => item.slug === product.slug);
  
  if (existingIndex >= 0) {
    cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
  } else {
    cart.push({
      slug: product.slug,
      name: product.name,
      price: product.price_ttc,
      image: product.images?.[0] || '',
      quantity: 1,
      category: 'pieces-detachees'
    });
  }
  
  localStorage.setItem('spa_cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
  
};

// Extract simplified name from product
const getSimplifiedName = (product) => {
  // Try to create a shorter, cleaner name
  let name = product.name;
  
  // Remove redundant SKU from name if present
  if (product.sku && name.includes(product.sku)) {
    name = name.replace(product.sku, '').trim();
    name = name.replace(/\(\s*\)/g, '').trim(); // Remove empty parentheses
  }
  
  // Remove very long technical references in parentheses at the end
  name = name.replace(/\s*\([^)]{15,}\)\s*$/, '').trim();
  
  return name;
};

// Get reference/SKU to display prominently
const getDisplayReference = (product) => {
  // Priority: sku > model > extract from name
  if (product.sku) return product.sku;
  if (product.model) return product.model;
  
  // Try to extract reference from name (usually in parentheses)
  const match = product.name.match(/\(([A-Z0-9-]+)\)/i);
  if (match) return match[1];
  
  return null;
};

const PartProductCard = ({ 
  product, 
  showCategoryBadge = true,
  linkPath = '/pieces-accessoires'
}) => {
  const reference = getDisplayReference(product);
  const simplifiedName = getSimplifiedName(product);
  const categoryBadge = CATEGORY_BADGES[product.sub_category];
  
  return (
    <Card 
      className="overflow-hidden border border-slate-200 hover:border-[#1E3A5F]/30 hover:shadow-lg transition-all group h-full flex flex-col" 
      data-testid={`product-${product.slug}`}
    >
      <Link to={`${linkPath}/${product.slug}`} className="flex flex-col h-full">
        {/* 1. IMAGE - Fixed height, object-contain, neutral background */}
        <div className="relative h-44 bg-white flex items-center justify-center border-b border-slate-100">
          {product.images?.[0] ? (
            <img 
              src={product.images[0]} 
              alt={product.name}
              className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <Wrench className="w-14 h-14 text-slate-200" />
          )}
          
          {/* Category badge - Top left, discreet */}
          {showCategoryBadge && categoryBadge && (
            <span className={`absolute top-2 left-2 text-[10px] font-medium px-2 py-0.5 rounded ${categoryBadge.color}`}>
              {categoryBadge.label}
            </span>
          )}
          
          {/* Stock badge - Top right */}
          {product.availability === 'en_stock' && (
            <span className="absolute top-2 right-2 bg-emerald-500 text-white text-[10px] font-medium px-2 py-0.5 rounded">
              En stock
            </span>
          )}
        </div>
        
        {/* Card Content */}
        <CardContent className="p-4 flex flex-col flex-1">
          {/* 2. REFERENCE - Most important, large and bold */}
          {reference && (
            <div className="mb-1">
              <span className="text-lg font-bold text-[#1E3A5F] tracking-wide">
                {reference}
              </span>
            </div>
          )}
          
          {/* 3. NAME - Simplified, smaller than reference */}
          <h3 className="text-sm text-slate-700 line-clamp-2 mb-1 group-hover:text-[#1E3A5F] transition-colors flex-1">
            {simplifiedName}
          </h3>
          
          {/* Brand - Very discreet */}
          {product.brand && (
            <span className="text-[10px] text-slate-400 uppercase tracking-wider mb-3">
              {product.brand}
            </span>
          )}
          
          {/* 4. PRICE - Clear TTC format */}
          <div className="mt-auto">
            {product.price_ttc ? (
              <div className="flex items-center justify-between gap-2">
                <span className="text-base font-bold text-slate-900">
                  {formatPrice(product.price_ttc)} <span className="text-xs font-normal text-slate-500">TTC</span>
                </span>
                
                {/* 5. SINGLE CTA - Add to cart */}
                <Button 
                  size="sm" 
                  onClick={(e) => addToCart(product, e)}
                  className="bg-[#1E3A5F] hover:bg-[#152C4A] h-9 px-4 text-xs font-medium"
                  data-testid={`add-to-cart-${product.slug}`}
                >
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <span className="text-sm text-slate-500 italic">Prix sur demande</span>
              </div>
            )}
          </div>
        </CardContent>
      </Link>
    </Card>
  );
};

export default PartProductCard;
